import { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import SEOMeta from '../../components/SEOMeta';
import landingPages, { ALL_STYLES, type LandingPage } from '../../data/seo-pages';
import { useDarkMode } from '../../hooks/useDarkMode';
import { WHATSAPP_CTA_URL } from '../../data/whatsapp';

const C = {
  cream: 'var(--cream)',
  warmWhite: 'var(--warm-white)',
  earth: 'var(--earth)',
  earthLight: 'var(--earth-light)',
  gold: 'var(--gold)',
  goldPale: 'var(--gold-pale)',
  amberDeep: 'var(--amber-deep)',
  beigeMid: 'var(--beige-mid)',
  textBody: 'var(--text-body)',
  textMuted: 'var(--text-muted)',
  beige: 'var(--beige-footer)',
};

// ── FAQ accordion ──────────────────────────────────────────────────────────────

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${C.beigeMid}`, padding: 0 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          gap: 16,
        }}
        aria-expanded={open}
      >
        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17, fontWeight: 500, color: C.earth, lineHeight: 1.4 }}>
          {question}
        </span>
        <span
          style={{
            flexShrink: 0,
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: open ? C.gold : C.goldPale,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s',
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
          >
            <path d="M2 5l5 5 5-5" stroke={open ? '#fff' : C.gold} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      {open && (
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: C.textBody, lineHeight: 1.7, paddingBottom: 20, margin: 0 }}>
          {answer}
        </p>
      )}
    </div>
  );
}

// ── Template principal ─────────────────────────────────────────────────────────

function LandingPageContent({ data }: { data: LandingPage }) {
  const isDark = useDarkMode();
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `Música Personalizada — ${data.ocasiao}${data.destinatario ? ` para ${data.destinatario}` : ''}`,
    description: data.description,
    brand: { '@type': 'Brand', name: 'Inspira Músicas' },
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '34.90',
      highPrice: '49.90',
      offerCount: '2',
      priceCurrency: 'BRL',
      availability: 'https://schema.org/InStock',
      deliveryLeadTime: { '@type': 'QuantitativeValue', value: '30', unitCode: 'MIN' },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <>
      <SEOMeta
        title={data.title}
        description={data.description}
        path={data.path}
        jsonLd={[productSchema, faqSchema]}
      />

      <div style={{ minHeight: '100vh', background: C.cream, fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── Header ── */}
        <header
          style={{
            background: C.warmWhite,
            borderBottom: `1px solid ${C.beigeMid}`,
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img src={isDark ? '/logo-dark.png' : '/logo-light.png'} alt="Inspira Músicas" style={{ height: 40, width: 'auto', objectFit: 'contain' }} />
          </a>
          <a
            href={WHATSAPP_CTA_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ background: C.gold, color: '#fff', padding: '10px 20px', borderRadius: 100, textDecoration: 'none', fontSize: 14, fontWeight: 500 }}
          >
            Criar música
          </a>
        </header>

        {/* ── HERO ── */}
        <section style={{ background: `linear-gradient(160deg, ${C.goldPale} 0%, ${C.cream} 100%)`, padding: '72px 24px 64px', textAlign: 'center' }}>
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <p style={{ fontSize: 13, fontWeight: 500, color: C.gold, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>
              {data.ocasiao}{data.destinatario ? ` — para ${data.destinatario}` : ''}
            </p>
            <h1
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(28px, 6vw, 50px)',
                fontWeight: 700,
                color: C.earth,
                margin: '0 0 20px',
                lineHeight: 1.2,
              }}
            >
              {data.h1}
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2.5vw, 18px)', color: C.earthLight, maxWidth: 580, margin: '0 auto 36px', lineHeight: 1.7 }}>
              {data.heroSubtitle}
            </p>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 32 }}>
              <a
                href={WHATSAPP_CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: C.gold, color: '#fff', padding: '16px 32px', borderRadius: 100, textDecoration: 'none', fontWeight: 600, fontSize: 16 }}
              >
                Criar música agora
              </a>
              <a
                href="/como-funciona"
                style={{ border: `1.5px solid ${C.earth}`, color: C.earth, padding: '16px 32px', borderRadius: 100, textDecoration: 'none', fontWeight: 500, fontSize: 16 }}
              >
                Ver como funciona
              </a>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[{ icon: '💰', text: 'A partir de R$ 34,90' }, { icon: '⚡', text: 'Tudo em até 30 min' }, { icon: '✅', text: 'Refação sem custo' }].map((s) => (
                <span key={s.text} style={{ background: C.warmWhite, border: `1px solid ${C.beigeMid}`, borderRadius: 100, padding: '8px 16px', fontSize: 13, color: C.textBody, fontWeight: 500 }}>
                  {s.icon} {s.text}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── COMO FUNCIONA ── */}
        <section style={{ padding: '72px 24px', background: C.warmWhite }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 700, color: C.earth, textAlign: 'center', marginBottom: 12 }}>
              Como funciona?
            </h2>
            <p style={{ textAlign: 'center', color: C.textMuted, fontSize: 16, marginBottom: 44, lineHeight: 1.6 }}>
              Três passos simples. Do pedido à música na sua mão em até 30 minutos.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
              {[
                { num: '1', icon: '💬', title: 'Conte a história', desc: 'Responda algumas perguntas sobre quem você quer homenagear: o jeito da pessoa, memórias especiais, o que ela representa para você. Quanto mais detalhes, mais especial fica a canção.' },
                { num: '2', icon: '🎵', title: 'Escolha o estilo musical', desc: 'Selecione entre mais de 25 gêneros musicais e defina se quer voz masculina ou feminina. A música vai ter a cara de quem vai receber.' },
                { num: '3', icon: '🎧', title: 'Aprove e receba', desc: 'Ouça uma prévia, aprove ou peça uma alteração e escolha entre a música ou a música com vídeo de fotos.' },
              ].map((step) => (
                <div key={step.num} style={{ background: C.cream, borderRadius: 16, padding: '28px 24px', border: `1px solid ${C.beigeMid}` }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.goldPale, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 16, color: C.gold, marginBottom: 12 }}>
                    {step.num}
                  </div>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>{step.icon}</div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: C.earth, marginBottom: 8 }}>{step.title}</h3>
                  <p style={{ fontSize: 14, color: C.textBody, lineHeight: 1.7, margin: 0 }}>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── POR QUE UMA MÚSICA? ── */}
        <section style={{ padding: '72px 24px', background: C.cream }}>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 700, color: C.earth, marginBottom: 28, lineHeight: 1.25 }}>
              Por que uma música personalizada para {data.ocasiao.toLowerCase()}
              {data.destinatario ? ` da sua ${data.destinatario}` : ''}?
            </h2>
            {data.whySection.map((para, i) => (
              <p key={i} style={{ fontSize: 16, color: C.textBody, lineHeight: 1.8, marginBottom: i < data.whySection.length - 1 ? 20 : 0 }}>
                {para}
              </p>
            ))}
          </div>
        </section>

        {/* ── COMPARAÇÃO DE PREÇO ── */}
        <section style={{ padding: '72px 24px', background: C.warmWhite }}>
          <div style={{ maxWidth: 820, margin: '0 auto' }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 700, color: C.earth, marginBottom: 16, lineHeight: 1.25 }}>
              Quanto custa uma música personalizada?
            </h2>
            <p style={{ fontSize: 16, color: C.textMuted, marginBottom: 36, lineHeight: 1.6 }}>
              {data.priceAnchor.intro}
            </p>

            <div style={{ overflowX: 'auto', marginBottom: 40 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: C.beigeMid }}>
                    {['Presente', 'Preço médio', 'Dura quanto tempo?', 'Emoção'].map((h) => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, color: C.earth, whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.priceAnchor.comparisons.map((row, i) => (
                    <tr key={row.label} style={{ background: i % 2 === 0 ? C.cream : C.warmWhite }}>
                      <td style={{ padding: '12px 16px', color: C.textBody }}>{row.label}</td>
                      <td style={{ padding: '12px 16px', color: C.textBody }}>{row.price}</td>
                      <td style={{ padding: '12px 16px', color: C.textBody }}>{row.duracao}</td>
                      <td style={{ padding: '12px 16px', color: C.textMuted }}>{row.emocao}</td>
                    </tr>
                  ))}
                  <tr style={{ background: C.goldPale }}>
                    <td style={{ padding: '14px 16px', color: C.earth, fontWeight: 700 }}>🎵 Música exclusiva</td>
                    <td style={{ padding: '14px 16px', color: C.gold, fontWeight: 700 }}>A partir de R$ 34,90</td>
                    <td style={{ padding: '14px 16px', color: C.earth, fontWeight: 700 }}>Para sempre</td>
                    <td style={{ padding: '14px 16px', color: C.gold, fontWeight: 700 }}>Máxima ✨</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ background: C.goldPale, border: `2px solid ${C.gold}`, borderRadius: 20, padding: '32px 28px', textAlign: 'center' }}>
              <p style={{ fontSize: 14, color: C.earthLight, marginBottom: 6 }}>A partir de</p>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(36px, 7vw, 56px)', fontWeight: 700, color: C.gold, lineHeight: 1, marginBottom: 8 }}>
                R$ 34,90
              </div>
              <p style={{ fontSize: 14, color: C.earthLight, marginBottom: 18 }}>Pagamento via PIX · Tudo pronto em até 30 minutos · Sem mensalidade</p>
              <p style={{ fontSize: 13, color: C.textMuted, marginBottom: 24 }}>
                🛡️ Garantia de 100% de satisfação — refazemos a música sem custo quando você pedir ajustes.
              </p>
              <a href={WHATSAPP_CTA_URL} target="_blank" rel="noopener noreferrer" style={{ background: C.gold, color: '#fff', padding: '14px 36px', borderRadius: 100, textDecoration: 'none', fontWeight: 600, fontSize: 15, display: 'inline-block' }}>
                Criar música agora
              </a>
            </div>
          </div>
        </section>

        {/* ── ESTILOS MUSICAIS ── */}
        <section style={{ padding: '72px 24px', background: C.cream }}>
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 700, color: C.earth, textAlign: 'center', marginBottom: 12 }}>
              25 estilos musicais disponíveis
            </h2>
            <p style={{ textAlign: 'center', color: C.textMuted, fontSize: 16, marginBottom: 36, lineHeight: 1.6 }}>
              Escolha o som que combina com quem vai receber.{' '}
              <strong style={{ color: C.amberDeep }}>{data.estiloDestaque}</strong> é o mais pedido para {data.ocasiao.toLowerCase()}.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
              {ALL_STYLES.map((style) => {
                const isDestaque = style === data.estiloDestaque;
                return (
                  <span
                    key={style}
                    style={{
                      background: isDestaque ? C.gold : C.goldPale,
                      border: `1.5px solid ${isDestaque ? C.gold : C.beigeMid}`,
                      borderRadius: 100,
                      padding: '8px 18px',
                      fontSize: 14,
                      fontWeight: isDestaque ? 600 : 500,
                      color: isDestaque ? '#fff' : C.amberDeep,
                      position: 'relative',
                    }}
                  >
                    {style}
                    {isDestaque && (
                      <span style={{ marginLeft: 6, fontSize: 11, background: 'rgba(255,255,255,0.25)', borderRadius: 4, padding: '1px 5px' }}>
                        Popular ★
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section style={{ padding: '72px 24px', background: C.warmWhite }}>
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(22px, 4vw, 34px)', fontWeight: 700, color: C.earth, marginBottom: 8 }}>
              Perguntas frequentes
            </h2>
            <p style={{ color: C.textMuted, fontSize: 16, marginBottom: 36, lineHeight: 1.6 }}>
              Dúvidas sobre a música personalizada para {data.ocasiao.toLowerCase()}
              {data.destinatario ? ` da sua ${data.destinatario}` : ''}.
            </p>
            {data.faqs.map((f, i) => (
              <FAQItem key={i} question={f.question} answer={f.answer} />
            ))}
            <p style={{ marginTop: 28, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>
              Mais dúvidas?{' '}
              <a href="/faq" style={{ color: C.gold, textDecoration: 'underline' }}>Veja todas as perguntas frequentes</a>{' '}
              ou fale conosco pelo WhatsApp.
            </p>
          </div>
        </section>

        {/* ── CTA FINAL ── */}
        <section style={{ padding: '80px 24px', background: `linear-gradient(160deg, ${C.goldPale} 0%, ${C.cream} 100%)`, textAlign: 'center' }}>
          <div style={{ maxWidth: 580, margin: '0 auto' }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(24px, 5vw, 38px)', fontWeight: 700, color: C.earth, marginBottom: 16, lineHeight: 1.2 }}>
              Ainda dá tempo.
            </h2>
            <p style={{ fontSize: 17, color: C.earthLight, lineHeight: 1.7, marginBottom: 32 }}>
              30 minutos e {data.possessivo} {data.destinatario ?? 'pessoa especial'} recebe a música.{' '}
              Um presente que fica para sempre.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href={WHATSAPP_CTA_URL} target="_blank" rel="noopener noreferrer" style={{ background: C.gold, color: '#fff', padding: '16px 36px', borderRadius: 100, textDecoration: 'none', fontWeight: 600, fontSize: 16 }}>
                Criar música agora
              </a>
            </div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer style={{ background: C.beige, padding: '24px', textAlign: 'center', fontSize: 13, color: C.textMuted }}>
          © 2025 Inspira Músicas — AUTOMAT TECNOLOGIA DA INFORMACAO LTDA · CNPJ 59.640.197/0001-76
          <br />
          <a href="/privacidade" style={{ color: C.textMuted, marginRight: 12 }}>Privacidade</a>
          <a href="/termos" style={{ color: C.textMuted }}>Termos</a>
        </footer>

      </div>
    </>
  );
}

// ── Wrapper com slug lookup (usado pela rota dinâmica) ─────────────────────────

export function LandingPageBySlug() {
  const { slug } = useParams<{ slug: string }>();
  const data = landingPages.find((p) => p.slug === slug);
  if (!data) return <Navigate to="/404" replace />;
  return <LandingPageContent data={data} />;
}

// ── Export direto com dados (usado pelas rotas estáticas) ──────────────────────

export default function LandingTemplate({ slug }: { slug: string }) {
  const data = landingPages.find((p) => p.slug === slug);
  if (!data) return <Navigate to="/404" replace />;
  return <LandingPageContent data={data} />;
}
